import React, { useEffect, useRef, useState } from 'react';
import { parseInlineMarkdown, renderPlainTextWithFormatting } from '../../../../../../utils/LearnTab/slideTextRenderer';
import { tUi } from '../../../../../../../../../utils/dq_lc';
import { HtmlContentColumn, HtmlContentWrapper, SlideTitle } from '../../../../../../styles/LearnTab/slideBase.styles';
import { CaseSectionStack, CaseSection, CaseSectionLabel } from './CaseStudyCard.styles';
import {
  LabChipRow, LabChip, LabStepList, LabMuted, LabPredict, LabWrites, LabArtifact, LabDocArtifact, LabAnswerKey,
  LabArtifactBar, LabKind, LabWhere, LabPlainList,
} from './HandsOnLabCard.styles';
import { OverviewChecklist } from './PracticeOverviewCard.styles';

export const textOf = (value) => (typeof value === 'string' ? value.trim() : '');
export const listOf = (value) => (Array.isArray(value) ? value : []);
// Prose of lab and overview slides. Server glossary spans go through the shared parser; text that only
// looks like HTML because of a placeholder such as <YOUR_API_KEY> stays plain. Otherwise only **bold** is
// markup: technical prose is full of snake_case names (GEMINI_API_KEY, research_helper.ipynb) that the
// shared parser would turn into italics.
const KNOWN_INLINE_TAG = /<\/?(span|strong|em|b|i|br|code|a)\b/i;
const BOLD = /(\*\*[^*]+\*\*)/g;
export const inline = (value) => {
  const text = textOf(value);
  if (!text) return null;
  if (/<[a-z][\s\S]*>/i.test(text)) return KNOWN_INLINE_TAG.test(text) ? parseInlineMarkdown(text) : text;
  return text.split(BOLD).map((part, i) => (
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  ));
};
export const SAFE_URL = /^https:\/\//;
// Artifacts the learner reads or fills in rather than runs.
const TEXT_ARTIFACT_KINDS = ['sample', 'template', 'checklist', 'prompt'];
// Step location code to i18n suffix: "code editor" -> codeEditor.
const whereKeyOf = (value) => textOf(value).toLowerCase().replace(/\s+(\w)/g, (_, c) => c.toUpperCase());

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    try { document.execCommand('copy'); } finally { document.body.removeChild(area); }
  }
};

const LinkList = ({ items }) => (
  <ul>
    {items.map((item, idx) => {
      const title = textOf(item?.title) || textOf(item?.url);
      const url = textOf(item?.url);
      return (
        <li key={item?.resource_id || idx}>
          {SAFE_URL.test(url)
            ? <a href={url} target="_blank" rel="noopener noreferrer">{title}</a>
            : title}
          {textOf(item?.kind) && <LabKind data-lab-link-kind>{textOf(item.kind)}</LabKind>}
        </li>
      );
    })}
  </ul>
);

export const HandsOnLabCard = ({
  slide,
  slideEdits = {},
  showEnglishContent = false,
  highlightWord,
  uiLocale = 'en-US',
}) => {
  const [copiedKey, setCopiedKey] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const origin = slide?._pbdq_origin || slide || {};

  const rawTitle = textOf(origin.lab_title) || slide?.SlideTitle || '';
  const titleEdit = slideEdits?.['title'];
  const displayTitle = showEnglishContent ? (titleEdit?.original || rawTitle) : (titleEdit?.edited || rawTitle);
  const kind = textOf(origin.lab_kind);
  const minutes = Number(origin.time_box_minutes);
  const stepStart = Number(origin.step_start) || 1;
  const steps = listOf(origin.steps).filter((step) => step && (typeof step === 'string' || textOf(step.action) || textOf(step.detail)));
  const artifacts = listOf(origin.artifacts).filter((artifact) => typeof artifact?.content === 'string' && artifact.content);
  const prerequisites = listOf(origin.prerequisites).map(textOf).filter(Boolean);
  const tools = listOf(origin.tools).filter((item) => textOf(item?.title) || textOf(item?.url));
  const resources = listOf(origin.resources).filter((item) => textOf(item?.title) || textOf(item?.url));
  const files = listOf(origin.files).filter((item) => textOf(item?.path));
  const troubleshooting = listOf(origin.troubleshooting).filter((item) => textOf(item?.symptom) || textOf(item?.fix));
  // Rubric criteria for the whole lab; the engine sends them on the last part of a split lab.
  const successCriteria = listOf(origin.success_criteria).map(textOf).filter(Boolean);

  // Known codes get a translated label; an unexpected code is shown as is.
  const labelOf = (key, fallback) => (tUi(key, uiLocale) === key ? fallback : tUi(key, uiLocale));

  const handleCopy = (key, text) => {
    copyText(text).then(() => {
      setCopiedKey(key);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  const renderArtifact = (artifact, key) => {
    if (textOf(artifact.kind) === 'answer_key') {
      return (
        <LabAnswerKey key={key} data-lab-answer-key>
          <summary>{tUi('slide.lab.showAnswerKey', uiLocale)}</summary>
          <pre data-lab-artifact><code>{artifact.content}</code></pre>
        </LabAnswerKey>
      );
    }
    const kindLabel = labelOf(`slide.lab.artifact.${textOf(artifact.kind)}`, textOf(artifact.kind));
    const Frame = TEXT_ARTIFACT_KINDS.includes(textOf(artifact.kind)) ? LabDocArtifact : LabArtifact;
    return (
      <Frame key={key} data-lab-artifact-kind={textOf(artifact.kind) || undefined}>
        <LabArtifactBar>
          <span data-lab-artifact-label>{[kindLabel, textOf(artifact.language)].filter(Boolean).join(' · ')}</span>
          <button type="button" data-lab-copy onClick={() => handleCopy(key, artifact.content)}>
            {tUi(copiedKey === key ? 'slide.lab.copied' : 'slide.lab.copy', uiLocale)}
          </button>
        </LabArtifactBar>
        <pre data-lab-artifact><code>{artifact.content}</code></pre>
      </Frame>
    );
  };

  // [key, content] in display order; key is also the i18n suffix and the data-lab-section value.
  const sections = [
    ['goal', inline(origin.goal)],
    ['prerequisites', prerequisites.length > 0 && (
      <ul>{prerequisites.map((item, idx) => <li key={idx}>{inline(item)}</li>)}</ul>
    )],
    ['files', files.length > 0 && (
      <LabPlainList>
        {files.map((file, idx) => (
          <li key={idx} data-lab-file>
            <code>{textOf(file.path)}</code>
            {textOf(file.purpose) && <> — {inline(file.purpose)}</>}
          </li>
        ))}
      </LabPlainList>
    )],
    ['tools', tools.length > 0 && <LinkList items={tools} />],
    ['steps', steps.length > 0 && (
      <LabStepList start={stepStart}>
        {steps.map((step, idx) => {
          const number = stepStart + idx;
          const action = typeof step === 'string' ? step : step.action;
          const detail = inline(step?.detail);
          const expected = inline(step?.expected_result);
          const explain = inline(step?.explain);
          const predict = inline(step?.predict);
          const writes = inline(step?.learner_writes);
          const reveal = inline(step?.reveal);
          const where = textOf(step?.where);
          return (
            <li key={number} data-lab-step={number}>
              {where && <LabWhere data-lab-where={where}>{labelOf(`slide.lab.where.${whereKeyOf(where)}`, where)}</LabWhere>}
              {textOf(action) && <strong>{inline(action)}</strong>}
              {explain && <LabMuted data-lab-explain>{tUi('slide.lab.explainPrefix', uiLocale)}: {explain}</LabMuted>}
              {predict && <LabPredict data-lab-predict>{tUi('slide.lab.predictPrefix', uiLocale)}: {predict}</LabPredict>}
              {writes && <LabWrites data-lab-writes>{tUi('slide.lab.youWritePrefix', uiLocale)}: {writes}</LabWrites>}
              {detail && <div>{detail}</div>}
              {expected && <LabMuted>{tUi('slide.lab.expectedResult', uiLocale)}: {expected}</LabMuted>}
              {reveal && (
                <LabAnswerKey data-lab-reveal>
                  <summary>{tUi('slide.lab.revealPrefix', uiLocale)}</summary>
                  <div>{reveal}</div>
                </LabAnswerKey>
              )}
              {artifacts.filter((artifact) => Number(artifact.step) === number)
                .map((artifact, aIdx) => renderArtifact(artifact, `${number}-${aIdx}`))}
            </li>
          );
        })}
      </LabStepList>
    )],
    ['checkpoint', inline(origin.checkpoint)],
    ['successCriteria', successCriteria.length > 0 && (
      <OverviewChecklist>{successCriteria.map((item, idx) => <li key={idx}>{inline(item)}</li>)}</OverviewChecklist>
    )],
    ['troubleshooting', troubleshooting.length > 0 && (
      <ul>
        {troubleshooting.map((item, idx) => (
          <li key={idx} data-lab-trouble>
            {textOf(item.symptom) && <strong>{inline(item.symptom)}</strong>}
            {textOf(item.symptom) && textOf(item.fix) && ' → '}
            {inline(item.fix)}
          </li>
        ))}
      </ul>
    )],
    ['deliverable', inline(origin.deliverable)],
    ['resources', resources.length > 0 && <LinkList items={resources} />],
  ];
  const filledSections = sections.filter(([, content]) => content);
  // Without steps and without a goal there is nothing to work through, so show the slide text instead.
  const fallback = steps.length === 0 && !textOf(origin.goal) ? textOf(slide?.SlideContent || origin.SlideContent) : '';

  return (
    <HtmlContentColumn data-lab-kind={kind || undefined}>
      {displayTitle && (
        <SlideTitle>
          {highlightWord ? highlightWord('title', displayTitle) : displayTitle}
        </SlideTitle>
      )}
      <HtmlContentWrapper>
        {(kind || minutes > 0) && (
          <LabChipRow>
            {kind && <LabChip data-lab-chip="kind">{labelOf(`slide.lab.kind.${kind}`, kind)}</LabChip>}
            {minutes > 0 && <LabChip data-lab-chip="time">{tUi('slide.lab.timeBox', uiLocale, { minutes })}</LabChip>}
          </LabChipRow>
        )}
        {filledSections.length > 0 && (
          <CaseSectionStack>
            {filledSections.map(([key, body]) => (
              <CaseSection key={key} data-lab-section={key}>
                <CaseSectionLabel data-lab-label>{tUi(`slide.lab.${key}`, uiLocale)}</CaseSectionLabel>
                {body}
              </CaseSection>
            ))}
          </CaseSectionStack>
        )}
        {fallback && (
          <div className="content-body" style={{ textAlign: 'left', width: '100%' }}>
            {renderPlainTextWithFormatting(fallback)}
          </div>
        )}
      </HtmlContentWrapper>
    </HtmlContentColumn>
  );
};

export default HandsOnLabCard;
